if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.setupDesktopArrows();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        this.setupThumbnailOverflow();
      }

      setupThumbnailOverflow() {
        const thumbnailsEl = this.elements.thumbnails;
        if (!thumbnailsEl?.slider) return;

        this.updateThumbnailOverflow = this.updateThumbnailOverflow.bind(this);
        thumbnailsEl.slider.addEventListener('scroll', debounce(this.updateThumbnailOverflow, 100));
        thumbnailsEl.addEventListener('slideChanged', this.updateThumbnailOverflow);

        const resizeObserver = new ResizeObserver(() => this.updateThumbnailOverflow());
        resizeObserver.observe(thumbnailsEl.slider);

        this.mql.addEventListener('change', this.updateThumbnailOverflow);
        this.updateThumbnailOverflow();
      }

      isThumbnailInView(thumbnail, slider) {
        const itemLeft = thumbnail.offsetLeft;
        const itemRight = itemLeft + thumbnail.clientWidth;
        const viewLeft = slider.scrollLeft;
        const viewRight = slider.scrollLeft + slider.clientWidth;
        return itemRight > viewLeft + 1 && itemLeft < viewRight - 1;
      }

      updateThumbnailOverflow() {
        const thumbnailsEl = this.elements.thumbnails;
        if (!thumbnailsEl?.slider) return;

        const slider = thumbnailsEl.slider;
        const items = Array.from(thumbnailsEl.querySelectorAll('.thumbnail-list__item[data-target]'));

        thumbnailsEl.querySelectorAll('.thumbnail__overflow').forEach((element) => element.remove());

        if (items.length < 2) return;

        const visibleItems = items.filter((item) => this.isThumbnailInView(item, slider));
        if (!visibleItems.length) return;

        const rightmostVisible = visibleItems[visibleItems.length - 1];
        const rightmostIndex = items.indexOf(rightmostVisible);
        const hiddenCount = items.length - rightmostIndex - 1;

        if (hiddenCount <= 0) return;

        const thumbnailButton = rightmostVisible.querySelector('.thumbnail');
        if (!thumbnailButton) return;

        const overflow = document.createElement('span');
        overflow.className = 'thumbnail__overflow';
        overflow.setAttribute('aria-hidden', 'true');
        overflow.textContent = `+${hiddenCount}`;
        thumbnailButton.appendChild(overflow);
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setupDesktopArrows() {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const sliderButtons = viewer.querySelector('.slider-buttons');
        if (!sliderButtons) return;

        const prevBtn = sliderButtons.querySelector('button[name="previous"]');
        const nextBtn = sliderButtons.querySelector('button[name="next"]');
        if (!prevBtn || !nextBtn) return;

        const handleDesktopArrow = (event) => {
          if (!this.mql.matches) return;
          if (!this.dataset.desktopLayout?.includes('thumbnail')) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          const slides = Array.from(viewer.querySelectorAll('.product__media-item[data-media-id]'));
          const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
          if (activeIndex === -1) return;

          const step = event.currentTarget.name === 'next' ? 1 : -1;
          const newIndex = activeIndex + step;
          if (newIndex < 0 || newIndex >= slides.length) return;

          this.setActiveMedia(slides[newIndex].dataset.mediaId, false);
          this.updateDesktopArrowState(slides, newIndex, prevBtn, nextBtn);
        };

        [prevBtn, nextBtn].forEach((button) => {
          button.addEventListener('click', handleDesktopArrow, true);
        });

        this.mql.addEventListener('change', () => {
          const slides = Array.from(viewer.querySelectorAll('.product__media-item[data-media-id]'));
          const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
          if (this.mql.matches && this.dataset.desktopLayout?.includes('thumbnail')) {
            this.updateDesktopArrowState(slides, activeIndex, prevBtn, nextBtn);
          } else {
            prevBtn.removeAttribute('disabled');
            nextBtn.removeAttribute('disabled');
          }
        });

        if (this.mql.matches && this.dataset.desktopLayout?.includes('thumbnail')) {
          const slides = Array.from(viewer.querySelectorAll('.product__media-item[data-media-id]'));
          const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
          this.updateDesktopArrowState(slides, activeIndex, prevBtn, nextBtn);
        }
      }

      updateDesktopArrowState(slides, activeIndex, prevBtn, nextBtn) {
        const currentElement = this.elements.viewer.querySelector('.slider-counter--current');
        if (currentElement && activeIndex >= 0) currentElement.textContent = activeIndex + 1;

        prevBtn.toggleAttribute('disabled', activeIndex <= 0);
        nextBtn.toggleAttribute('disabled', activeIndex >= slides.length - 1);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.mql.matches && this.dataset.desktopLayout?.includes('thumbnail')) {
          const sliderButtons = this.elements.viewer.querySelector('.slider-buttons');
          if (sliderButtons) {
            const prevBtn = sliderButtons.querySelector('button[name="previous"]');
            const nextBtn = sliderButtons.querySelector('button[name="next"]');
            const slides = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
            const activeIndex = slides.findIndex((slide) => slide.classList.contains('is-active'));
            if (prevBtn && nextBtn) this.updateDesktopArrowState(slides, activeIndex, prevBtn, nextBtn);
          }
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {
          this.updateThumbnailOverflow();
          return;
        }

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        this.updateThumbnailOverflow();
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
